import type { AxiosResponse } from 'axios';

/**
 * Drop every prefetched page once something has been written.
 *
 * The sidebar prefetches its links (`NavMain.vue`), and Inertia keeps a
 * prefetched page for 30 seconds with no idea the data behind it has changed.
 * So a save followed by a click on a link that had already been hovered
 * showed the page as it was BEFORE the save — a currency added in Pricing
 * settings missing from the Rate Manager's switcher until a full reload. It is
 * not specific to one screen: any write, then any prefetched page.
 *
 * Nothing is lost by flushing on a write: the next click fetches fresh, and
 * hovering prefetches again. Reads (GETs) and prefetches themselves leave the
 * cache alone, or prefetching would do nothing at all.
 *
 * Both write paths are covered: an Inertia visit (forms, `router.put`…), and a
 * direct axios request — the Rate Manager's inline edits, rules and price
 * calculator write that way.
 */

/**
 * The part of Inertia's `finish` event this reads. Typed by shape rather than
 * as `GlobalEvent<'finish'>`: `@inertiajs/vue3` bundles its own copy of
 * `@inertiajs/core`, and the two declarations of that type do not match.
 */
export interface FinishedVisitEvent {
    detail: { visit: { completed: boolean; prefetch: boolean; method: string } };
}

/** Whether a request changed something — anything but a GET (or a HEAD). */
export const isWrite = (method: string | undefined): boolean => method !== undefined && !['get', 'head'].includes(method.toLowerCase());

/** For `router.on('finish', …)`: a completed, non-prefetch Inertia write. */
export const createVisitFlusher =
    (flushAll: () => void) =>
    (event: FinishedVisitEvent): void => {
        const { visit } = event.detail;

        if (visit.completed && !visit.prefetch && isWrite(visit.method)) {
            flushAll();
        }
    };

/** For `axios.interceptors.response.use(…)`: a successful direct write. */
export const createResponseFlusher =
    (flushAll: () => void) =>
    <T extends AxiosResponse>(response: T): T => {
        if (isWrite(response.config.method)) {
            flushAll();
        }

        return response;
    };
